/**
 * Per-component Cognara analysis + chat.
 *
 * `<ExplainButton>` sits next to any component of a review (a claim
 * opportunity, a risk, the LD position …). Opening it starts — or re-opens —
 * a session held against that review version, so the explanation and the whole
 * conversation come straight back the next time without spending another
 * analysis.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sparkles, Loader2, Send, RefreshCw, Download, FileDown, MessageSquare, ChevronDown, Gavel, ShieldAlert } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import api from '@/lib/api';

const MD = {
  h1: ({ children }) => <h3 className="text-sm font-semibold text-foreground mt-4 mb-1.5">{children}</h3>,
  h2: ({ children }) => <h3 className="text-sm font-semibold text-foreground mt-4 mb-1.5">{children}</h3>,
  h3: ({ children }) => <h4 className="text-xs font-semibold text-purple-300 mt-3 mb-1">{children}</h4>,
  h4: ({ children }) => <h4 className="text-xs font-semibold text-purple-300 mt-3 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-xs leading-relaxed text-foreground/85 mb-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 space-y-1 mb-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 space-y-1 mb-2">{children}</ol>,
  li: ({ children }) => <li className="text-xs leading-relaxed text-foreground/85">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic text-foreground/70">{children}</em>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-purple-500/50 pl-3 italic text-xs text-foreground/70 my-2">{children}</blockquote>,
  code: ({ children }) => <code className="text-[11px] bg-muted px-1 py-0.5 rounded font-mono">{children}</code>,
  table: ({ children }) => <div className="overflow-x-auto my-2"><table className="w-full text-[11px] border border-border">{children}</table></div>,
  th: ({ children }) => <th className="text-left px-2 py-1 bg-muted/60 font-semibold">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1 border-t border-border align-top">{children}</td>,
  a: ({ children, href }) => <a href={href} className="text-sky-400 underline">{children}</a>,
};

const Md = ({ children }) => <ReactMarkdown components={MD}>{children}</ReactMarkdown>;

const ReviewCtx = createContext({ contractId: null, reviewId: null });

export const CognaraReviewProvider = ReviewCtx.Provider;

export function ExplainButton({ kind, componentKey, label, component, compact = false }) {
  const { contractId, reviewId } = useContext(ReviewCtx);
  const [open, setOpen] = useState(false);
  if (!contractId || !reviewId || !componentKey) return null;
  return (
    <>
      <Button
        size="sm" variant="ghost"
        className={`text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 ${compact ? 'h-6 px-1.5' : 'h-7 px-2'}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`explain-btn-${kind}-${componentKey}`}
        title="Ask Cognara to explain this in detail"
      >
        <Sparkles className="w-3 h-3" />
        {!compact && <span className="ml-1 text-[10px]">Explain</span>}
      </Button>
      {open && (
        <ComponentAnalysisPanel
          open={open} onOpenChange={setOpen}
          contractId={contractId} reviewId={reviewId}
          kind={kind} componentKey={componentKey} label={label} component={component}
        />
      )}
    </>
  );
}

function ComponentAnalysisPanel({ open, onOpenChange, contractId, reviewId, kind, componentKey, label, component }) {
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(true);
  const [openError, setOpenError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const componentRef = useRef(component);
  componentRef.current = component;

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const poll = useCallback((sessionId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/cognara-chat/sessions/${sessionId}`);
        setSession(data.session);
        if (data.session?.status !== 'RUNNING') { stopPoll(); setBusy(false); }
      } catch { stopPoll(); setBusy(false); }
    }, 3000);
  }, []);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.post('/cognara-chat/sessions', {
          contract_id: contractId, review_id: reviewId, kind,
          component_key: componentKey, label, component: componentRef.current,
        });
        if (dead) return;
        if (!data.session) throw new Error('The analysis session could not be opened');
        setSession(data.session);
        if (data.session?.status === 'RUNNING') poll(data.session.session_id);
        else setBusy(false);
      } catch (e) {
        if (!dead) {
          setBusy(false);
          const msg = e?.response?.data?.detail || e?.message || 'Could not open the analysis';
          setOpenError(msg);
          toast.error(msg);
        }
      }
    })();
    return () => { dead = true; stopPoll(); };
  }, [contractId, reviewId, kind, componentKey, label, poll]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [session?.messages?.length, session?.explanation]);

  const ask = async () => {
    const q = question.trim();
    if (!q || !session) return;
    setQuestion('');
    setSession(s => ({ ...s, status: 'RUNNING', messages: [...(s.messages || []), { role: 'user', content: q }] }));
    setBusy(true);
    try {
      await api.post(`/cognara-chat/sessions/${session.session_id}/ask`, { question: q });
      poll(session.session_id);
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || 'Cognara could not answer that');
    }
  };

  const reanalyse = async () => {
    if (!session) return;
    setBusy(true);
    setSession(s => ({ ...s, status: 'RUNNING' }));
    try {
      await api.post(`/cognara-chat/sessions/${session.session_id}/reanalyse`);
      poll(session.session_id);
    } catch { setBusy(false); toast.error('Could not re-analyse'); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/cognara-chat/sessions/${session.session_id}/export?format=${fmt}`, { responseType: 'blob' });
      const { saveAs } = await import('file-saver');
      const mime = fmt === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      saveAs(new Blob([res.data], { type: mime }), `Cognara-Analysis-${componentKey}.${fmt}`);
    } catch { toast.error('Export failed'); }
  };

  const raise = async (target) => {
    try {
      const { data } = await api.post(`/cognara-chat/sessions/${session.session_id}/raise`, { target });
      toast.success(target === 'CLAIM'
        ? `Claim ${data.claim_number} created as a draft`
        : `Added to the project RAID log${data.raid_number ? ` as ${data.raid_number}` : ''}`);
      setSession(s => ({ ...s, raised: { target, ref: data.claim_number || data.raid_number || '' } }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not raise this'); }
  };

  const running = session?.status === 'RUNNING' || busy;

  useEffect(() => {
    if (!running) { setElapsed(0); return undefined; }
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="component-analysis-panel">
        <SheetHeader className="px-5 py-4 border-b border-border shrink-0">
          <SheetTitle className="text-base flex items-center gap-2 pr-8">
            <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
            <span className="truncate">{component?.title || label || 'Component analysis'}</span>
          </SheetTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[9px]">{label}</Badge>
            <Badge variant="outline" className="text-[9px] font-mono">{componentKey}</Badge>
            {session?.review_version && <Badge variant="outline" className="text-[9px]">review v{session.review_version}</Badge>}
            {session?.messages?.length > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />{session.messages.length} message(s) in this session
              </span>
            )}
            {session?.raised && (
              <Badge className="text-[9px] bg-emerald-500/20 text-emerald-400">
                raised · {session.raised.target}{session.raised.ref ? ` ${session.raised.ref}` : ''}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="component-analysis-body">
          {openError && (
            <div className="text-xs text-red-400 border border-red-500/30 rounded-lg p-3"
                 data-testid="component-analysis-open-error">
              {openError}. Close this and open it again — if it keeps happening, the review may need
              re-running.
            </div>
          )}
          {!session?.explanation && running && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="component-analysis-loading">
              <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
              {session?.stage === 'THINKING'
                ? 'Cognara is working through this item against the contract'
                : 'Cognara is pulling up what it has already read on these documents'}
              <span className="font-mono text-[11px] text-foreground/70" data-testid="component-analysis-elapsed">
                {elapsed}s
              </span>
            </div>
          )}
          {session?.status === 'FAILED' && (
            <div className="text-xs text-red-400 border border-red-500/30 rounded-lg p-3" data-testid="component-analysis-error">
              {session.explanation
                ? <>Cognara could not answer that. {session.error}</>
                : <>Cognara could not complete this analysis. {session.error}</>}
              {!session.explanation && (
                <Button size="sm" variant="outline" className="ml-2 h-6 text-[10px]" onClick={reanalyse}>Try again</Button>
              )}
            </div>
          )}
          {session?.explanation && (
            <article className="max-w-none" data-testid="component-analysis-explanation">
              <Md>{session.explanation}</Md>
            </article>
          )}

          {(session?.messages || []).map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : ''} data-testid={`component-chat-msg-${i}`}>
              <div className={m.role === 'user'
                ? 'bg-purple-500/15 border border-purple-500/30 rounded-xl rounded-br-sm px-3 py-2 max-w-[85%] text-xs text-foreground'
                : 'max-w-none border-t border-border pt-3'}>
                {m.role === 'user' ? m.content : <Md>{m.content}</Md>}
              </div>
            </div>
          ))}
          {session?.explanation && running && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin text-purple-400" />Cognara is thinking…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-border px-5 py-3 space-y-2 shrink-0">
          <div className="flex items-center gap-2">
            <Textarea
              rows={2} value={question} onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ask(); }}
              placeholder="Ask anything about this item — evidence, quantum, notices, strategy…"
              className="text-xs resize-none" disabled={running}
              data-testid="component-chat-input"
            />
            <Button size="sm" onClick={ask} disabled={running || !question.trim()}
                    className="bg-purple-600 hover:bg-purple-700 h-9" data-testid="component-chat-send">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={reanalyse} disabled={running}
                    data-testid="component-reanalyse">
              <RefreshCw className="w-3 h-3 mr-1" />Re-analyse
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => download('docx')}
                    disabled={!session?.explanation} data-testid="component-export-docx">
              <Download className="w-3 h-3 mr-1" />Word
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => download('pdf')}
                    disabled={!session?.explanation} data-testid="component-export-pdf">
              <FileDown className="w-3 h-3 mr-1" />PDF
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 text-[10px] ml-auto text-emerald-400"
                        disabled={!session?.explanation || !!session?.raised} data-testid="component-raise-menu">
                  {session?.raised ? `Raised · ${session.raised.ref || session.raised.target}` : <>Raise this <ChevronDown className="w-3 h-3 ml-1" /></>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => raise('CLAIM')} data-testid="component-raise-claim">
                  <Gavel className="w-3 h-3 mr-2" />Create a draft claim
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => raise('RAID')} data-testid="component-raise-raid">
                  <ShieldAlert className="w-3 h-3 mr-2" />Add to the RAID log
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-[9px] text-muted-foreground/70">
            Advisory only — Capexara AI is not liable for decisions taken in reliance on this analysis.
            This session is saved against review v{session?.review_version || '—'}.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
